import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@safe-minds-ai/ui';
import {
  CheckCircle2,
  Download,
  FileCheck2,
  Mail,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  ShieldCheck,
  Trash2,
  Users2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUser, CreateUserDto, downloadParentEmailExport, fetchUsers, getAdminChildDisplayName, ParentProfile, ChildProfile, resendUserConsentEmail, resendUserVerificationEmail, updateUser, User, UsersListResponse } from '../api';
import { useToast } from '../toast';

type RoleFilter = 'ALL' | User['role'];
type DeletedFilter = 'active' | 'deleted' | 'all';
type ApprovalFilter = 'ALL' | 'true' | 'false';
type SortBy = 'createdAt' | 'name' | 'role' | 'status' | 'deletedAt';
type SortOrder = 'asc' | 'desc';

type ChildProfileFormState = { nickname: string; firstName: string; lastName: string; age: string; grade: string };

const roleLabels: Record<User['role'], string> = {
  ADMIN: 'Admin',
  PARENT: 'Parent',
  CHILD: 'Child',
};

const initialParentProfile: ParentProfile = { firstName: '', lastName: '' };
const initialChildProfile: ChildProfileFormState = { nickname: '', firstName: '', lastName: '', age: '', grade: '' };

export default function UsersRoute(): JSX.Element {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('ALL');
  const [deletedFilter, setDeletedFilter] = useState<DeletedFilter>('active');
  const [approvalFilter, setApprovalFilter] = useState<ApprovalFilter>('ALL');
  const [parentFilterId, setParentFilterId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortBy>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [pageOffset, setPageOffset] = useState(0);
  const [pageLimit] = useState(25);
  const [pagination, setPagination] = useState<UsersListResponse['pagination'] | null>(null);
  const [stats, setStats] = useState<UsersListResponse['stats'] | null>(null);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<User['role']>('PARENT');
  const [newParentProfile, setNewParentProfile] = useState<ParentProfile>({ ...initialParentProfile });
  const [newChildProfile, setNewChildProfile] = useState<ChildProfileFormState>({ ...initialChildProfile });
  const [newChildParentId, setNewChildParentId] = useState('');
  const [addUserPending, setAddUserPending] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusActionPending, setStatusActionPending] = useState(false);
  const [exportPending, setExportPending] = useState<'all' | 'marketing' | null>(null);
  const [resendVerificationPendingId, setResendVerificationPendingId] = useState<string | null>(null);
  const [resendConsentPendingId, setResendConsentPendingId] = useState<string | null>(null);
  const resendVerificationInFlightRef = useRef(new Set<string>());
  const resendConsentInFlightRef = useRef(new Set<string>());

  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const parentOptions = useMemo(
    () => users.filter((u) => u.role === 'PARENT' && !u.isDeleted),
    [users]
  );

  const roleStats = useMemo(() => {
    if (stats) {
      return {
        total: stats.total,
        ADMIN: stats.ADMIN,
        PARENT: stats.PARENT,
        CHILD: stats.CHILD,
        disabled: stats.deleted,
      };
    }
    return users.reduce(
      (acc, user) => {
        acc.total += 1;
        acc[user.role] += 1;
        if (user.isDeleted) acc.disabled += 1;
        return acc;
      },
      { total: 0, ADMIN: 0, PARENT: 0, CHILD: 0, disabled: 0 }
    );
  }, [users, stats]);

  const filteredUsers = useMemo(() => {
    return users;
  }, [users]);

  const showParentColumn = roleFilter === 'ALL' || roleFilter === 'CHILD' || Boolean(parentFilterId);

  const getUsers = async () => {
    try {
      setLoading(true);
      const data = await fetchUsers({
        role: roleFilter === 'ALL' ? undefined : roleFilter,
        deletedFilter,
        approved: approvalFilter,
        parentId: parentFilterId ?? undefined,
        search: searchTerm || undefined,
        sortBy,
        sortOrder,
        limit: pageLimit,
        offset: pageOffset,
      });
      const items = Array.isArray(data) ? data : data.items ?? [];
      const summary = Array.isArray(data as unknown) ? null : (data as UsersListResponse).stats ?? null;
      setUsers(items);
      setStats(summary);
      setPagination(Array.isArray(data as unknown) ? null : (data as UsersListResponse).pagination ?? null);
      setError(null);
    } catch (err: any) {
      setError('Failed to fetch users.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (audience: 'all' | 'marketing') => {
    try {
      setExportPending(audience);
      await downloadParentEmailExport(audience);
      toast.push({
        title: 'Export started',
        description: audience === 'marketing' ? 'Marketing opted-in parent CSV downloaded.' : 'All parent users CSV downloaded.',
        variant: 'success',
      });
    } catch (err) {
      toast.push({
        title: 'Export failed',
        description: err instanceof Error ? err.message : 'Unable to download parent email export.',
        variant: 'error',
      });
    } finally {
      setExportPending(null);
    }
  };

  useEffect(() => {
    getUsers();
  }, [roleFilter, deletedFilter, approvalFilter, parentFilterId, sortBy, sortOrder, pageOffset, searchTerm]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') ?? '';
    setSearchTerm(query);
  }, [location.search]);

  const resetAddForm = () => {
    setNewUserEmail('');
    setNewUserPassword('');
    setNewUserRole('PARENT');
    setNewParentProfile({ ...initialParentProfile });
    setNewChildProfile({ ...initialChildProfile });
    setNewChildParentId('');
  };

  const handleAddUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddUserPending(true);

    try {
      const payload: CreateUserDto = {
        role: newUserRole,
      };

      if (newUserRole === 'PARENT') {
        payload.email = newUserEmail;
        payload.password = newUserPassword;
        payload.parentProfile = {
          firstName: newParentProfile.firstName.trim(),
          lastName: newParentProfile.lastName.trim(),
        };
      } else if (newUserRole === 'CHILD') {
        if (!newChildParentId) {
          throw new Error('Select a parent for the child account.');
        }
        if (!newChildProfile.nickname.trim()) {
          throw new Error('Child nickname is required.');
        }
        payload.parentUserId = newChildParentId;
        payload.childProfile = {
          nickname: newChildProfile.nickname.trim(),
          firstName: newChildProfile.firstName.trim(),
          lastName: newChildProfile.lastName.trim(),
          age: Number(newChildProfile.age),
          grade: newChildProfile.grade ? Number(newChildProfile.grade) : undefined,
        } as ChildProfile;
      } else {
        payload.email = newUserEmail;
        payload.password = newUserPassword;
      }

      await createUser(payload);
      await getUsers();
      toast.push({ title: 'Success', description: `Created ${roleLabels[newUserRole]} account.`, variant: 'success' });
      setIsAddDialogOpen(false);
      resetAddForm();
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to add user.';
      toast.push({ title: 'Error', description: message, variant: 'error' });
      console.error(err);
    } finally {
      setAddUserPending(false);
    }
  };

  const handleRestore = async (user: User) => {
    try {
      await updateUser(user.id, { enable: true });
      await getUsers();
      toast.push({
        title: 'Account restored',
        description: `${user.email || user.name || 'User'} is now active.`,
        variant: 'success',
      });
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to restore account.';
      toast.push({ title: 'Error', description: message, variant: 'error' });
      console.error(err);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await updateUser(deleteTarget.id, { disable: true });
      await getUsers();
      toast.push({
        title: 'Account deleted',
        description: `${deleteTarget.email || deleteTarget.name || 'User'} is now deleted.`,
        variant: 'success',
      });
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to delete account.';
      toast.push({ title: 'Error', description: message, variant: 'error' });
      console.error(err);
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleApprove = async (user: User) => {
    if (user.role === 'PARENT' && user.isVerified === false) {
      toast.push({
        title: 'Email verification required',
        description: 'Parent accounts must verify their email before approval.',
        variant: 'error',
      });
      return;
    }
    try {
      setStatusActionPending(true);
      await updateUser(user.id, { approved: true });
      await getUsers();
      toast.push({
        title: 'Account approved',
        description: `${user.email || user.name || 'User'} can now access the app.`,
        variant: 'success',
      });
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to approve account.';
      toast.push({ title: 'Error', description: message, variant: 'error' });
      console.error(err);
    } finally {
      setStatusActionPending(false);
    }
  };

  const handleResendVerification = async (user: User) => {
    if (resendVerificationInFlightRef.current.has(user.id)) {
      return;
    }
    try {
      resendVerificationInFlightRef.current.add(user.id);
      setResendVerificationPendingId(user.id);
      const result = await resendUserVerificationEmail(user.id);
      toast.push({
        title: 'Verification email sent',
        description: result.message,
        variant: 'success',
      });
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to resend verification email.';
      toast.push({ title: 'Error', description: message, variant: 'error' });
      console.error(err);
    } finally {
      resendVerificationInFlightRef.current.delete(user.id);
      setResendVerificationPendingId(null);
    }
  };

  const handleResendConsent = async (user: User) => {
    if (resendConsentInFlightRef.current.has(user.id)) {
      return;
    }
    if (user.role !== 'PARENT' || user.isVerified === false || user.approved !== false) {
      toast.push({
        title: 'Consent link not available',
        description: 'Consent links can only be resent for verified parent accounts that are still pending consent.',
        variant: 'error',
      });
      return;
    }
    try {
      resendConsentInFlightRef.current.add(user.id);
      setResendConsentPendingId(user.id);
      const result = await resendUserConsentEmail(user.id);
      toast.push({
        title: 'Consent link sent',
        description: result.message,
        variant: 'success',
      });
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to resend consent link.';
      toast.push({ title: 'Error', description: message, variant: 'error' });
      console.error(err);
    } finally {
      resendConsentInFlightRef.current.delete(user.id);
      setResendConsentPendingId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <SkeletonHeader />
        <Card>
          <CardContent className="py-16 text-center text-slate-500 dark:text-slate-300">Loading users…</CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <SkeletonHeader />
        <Card>
          <CardContent className="py-16 text-center text-destructive">{error}</CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary/70">Accounts</p>
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">User directory</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300">Search parents, admins, and child accounts.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="rounded-full px-4" disabled={Boolean(exportPending)} onClick={() => handleExport('all')}>
            <Download className="mr-2 h-4 w-4" />
            All parents
          </Button>
          <Button variant="outline" className="rounded-full px-4" disabled={Boolean(exportPending)} onClick={() => handleExport('marketing')}>
            <Download className="mr-2 h-4 w-4" />
            Marketing opted-in
          </Button>
        <Dialog open={isAddDialogOpen} onOpenChange={(open) => { setIsAddDialogOpen(open); if (!open) resetAddForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full px-5">
              <Plus className="mr-2 h-4 w-4" />
              Add account
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle>Create account</DialogTitle>
              <DialogDescription>Provision a parent, admin, or child.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddUserSubmit} className="space-y-4">
              {newUserRole === 'CHILD' ? (
                <div className="rounded-2xl border border-slate-200/60 bg-slate-50/80 p-4 text-sm text-slate-500 dark:border-slate-700/50 dark:bg-slate-900/70 dark:text-slate-300">
                  Child accounts sign in with access codes, so no email or password is required.
                </div>
              ) : (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="newEmail">Email</Label>
                    <Input id="newEmail" type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} required />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="newPassword">Temporary password</Label>
                    <Input id="newPassword" type="password" value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} required />
                  </div>
                </>
              )}
              <div className="grid gap-2">
                <Label>Role</Label>
                <Select value={newUserRole} onValueChange={(value) => setNewUserRole(value as User['role'])}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                    <SelectItem value="PARENT">Parent</SelectItem>
                    <SelectItem value="CHILD">Child</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newUserRole === 'PARENT' && (
                <div className="grid gap-3 rounded-2xl border border-slate-200 dark:border-slate-700/60 p-4">
                  <Label className="text-xs uppercase text-slate-500 dark:text-slate-400">Parent profile</Label>
                  <div className="grid gap-2">
                    <Label htmlFor="parentFirst">First name</Label>
                    <Input id="parentFirst" value={newParentProfile.firstName} onChange={(e) => setNewParentProfile((prev) => ({ ...prev, firstName: e.target.value }))} required />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="parentLast">Last name</Label>
                    <Input id="parentLast" value={newParentProfile.lastName} onChange={(e) => setNewParentProfile((prev) => ({ ...prev, lastName: e.target.value }))} required />
                  </div>
                </div>
              )}

              {newUserRole === 'CHILD' && (
                <div className="grid gap-3 rounded-2xl border border-slate-200 dark:border-slate-700/60 p-4">
                  <Label className="text-xs uppercase text-slate-500 dark:text-slate-400">Child profile</Label>
                  <div className="grid gap-2">
                    <Label>Attach to parent</Label>
                    <Select value={newChildParentId} onValueChange={setNewChildParentId}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select parent" />
                      </SelectTrigger>
                      <SelectContent>
                        {parentOptions.map((parent) => (
                          <SelectItem key={parent.id} value={parent.id}>
                            {parent.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="childNickname">Nickname</Label>
                    <Input id="childNickname" value={newChildProfile.nickname} onChange={(e) => setNewChildProfile((prev) => ({ ...prev, nickname: e.target.value }))} required />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="childFirst">First name</Label>
                    <Input id="childFirst" value={newChildProfile.firstName} onChange={(e) => setNewChildProfile((prev) => ({ ...prev, firstName: e.target.value }))} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="childLast">Last name</Label>
                    <Input id="childLast" value={newChildProfile.lastName} onChange={(e) => setNewChildProfile((prev) => ({ ...prev, lastName: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-2">
                      <Label htmlFor="childAge">Age</Label>
                      <Input id="childAge" type="number" min={4} max={18} value={newChildProfile.age} onChange={(e) => setNewChildProfile((prev) => ({ ...prev, age: e.target.value }))} required />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="childGrade">Grade</Label>
                      <Input id="childGrade" type="number" min={1} max={12} value={newChildProfile.grade} onChange={(e) => setNewChildProfile((prev) => ({ ...prev, grade: e.target.value }))} />
                    </div>
                  </div>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={addUserPending}>
                {addUserPending ? 'Creating…' : 'Create account'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-5">
        <StatCard title="Total" value={roleStats.total} icon={<Users2 className="h-4 w-4" />} />
        <StatCard title="Parents" value={roleStats.PARENT} />
        <StatCard title="Admins" value={roleStats.ADMIN} />
        <StatCard title="Children" value={roleStats.CHILD} />
        <StatCard title="Deleted" value={roleStats.disabled} icon={<ShieldCheck className="h-4 w-4" />} />
      </div>

      <Card className="overflow-hidden border border-slate-100 bg-white/90 shadow-sm dark:border-slate-700/40 dark:bg-slate-900/80">
        <CardHeader className="space-y-4 border-b border-slate-100 bg-white/70 px-5 py-5 dark:border-slate-700/40 dark:bg-slate-900/70">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <CardTitle className="text-2xl">Directory</CardTitle>
              <CardDescription>Search, filter, and manage accounts.</CardDescription>
            </div>
            {pagination ? (
              <div className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-300">
                {pagination.total} results
              </div>
            ) : null}
          </div>
          <div className="flex flex-col gap-2 rounded-2xl border border-slate-200/70 bg-slate-50/70 p-2 dark:border-slate-700/50 dark:bg-slate-950/20 lg:flex-row lg:items-center">
            <div className="relative min-w-[220px] flex-1 lg:max-w-[380px]">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-300" />
              <Input
                placeholder="Search by email, ID, or domain"
                value={searchTerm}
                onChange={(e) => {
                  const next = e.target.value;
                  setSearchTerm(next);
                  setPageOffset(0);
                  const params = new URLSearchParams(location.search);
                  if (next) {
                    params.set('q', next);
                  } else {
                    params.delete('q');
                  }
                  navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`, {
                    replace: true,
                  });
                }}
                className="h-10 rounded-xl border-slate-200 bg-white pl-10 text-sm shadow-none focus-visible:border-blue-400 focus-visible:ring-2 focus-visible:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-100 dark:placeholder:text-slate-400"
              />
            </div>
            <Select value={roleFilter} onValueChange={(value) => { setRoleFilter(value as RoleFilter); setPageOffset(0); }}>
              <SelectTrigger className="h-10 rounded-xl border-slate-200 bg-white px-3 text-sm shadow-none lg:w-36 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-100">
                <SelectValue placeholder="All roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All roles</SelectItem>
                <SelectItem value="PARENT">Parents</SelectItem>
                <SelectItem value="ADMIN">Admins</SelectItem>
                <SelectItem value="CHILD">Children</SelectItem>
              </SelectContent>
            </Select>
            <Select value={deletedFilter} onValueChange={(value) => {
              setDeletedFilter(value as DeletedFilter);
              setPageOffset(0);
            }}>
              <SelectTrigger className="h-10 rounded-xl border-slate-200 bg-white px-3 text-sm shadow-none lg:w-40 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-100">
                <SelectValue placeholder="Active only" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="deleted">Deleted only</SelectItem>
                <SelectItem value="all">All (active + deleted)</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={approvalFilter}
              onValueChange={(value) => {
                setApprovalFilter(value as ApprovalFilter);
                setPageOffset(0);
              }}
            >
              <SelectTrigger className="h-10 rounded-xl border-slate-200 bg-white px-3 text-sm shadow-none lg:w-44 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-100">
                <SelectValue placeholder="All approval states" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All approval states</SelectItem>
                <SelectItem value="false">Pending</SelectItem>
                <SelectItem value="true">Approved</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={`${sortBy}:${sortOrder}`}
              onValueChange={(value) => {
                const [nextSort, nextOrder] = value.split(':');
                setSortBy(nextSort as SortBy);
                setSortOrder(nextOrder as SortOrder);
              }}
            >
              <SelectTrigger className="h-10 rounded-xl border-slate-200 bg-white px-3 text-sm shadow-none lg:w-44 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-100">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt:desc">Newest created</SelectItem>
                <SelectItem value="createdAt:asc">Oldest created</SelectItem>
                <SelectItem value="name:asc">Name (A–Z)</SelectItem>
                <SelectItem value="name:desc">Name (Z–A)</SelectItem>
                <SelectItem value="role:asc">Role (A–Z)</SelectItem>
                <SelectItem value="status:asc">Status (Active → Deleted)</SelectItem>
                <SelectItem value="deletedAt:desc">Recently deleted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {parentFilterId ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-300">
              <Badge className="rounded-full bg-blue-600/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-200">
                Viewing children of selected parent
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                className="rounded-full"
                onClick={() => {
                  setParentFilterId(null);
                  setRoleFilter('ALL');
                  setPageOffset(0);
                }}
              >
                Clear filter
              </Button>
            </div>
          ) : null}
        </CardHeader>
        <CardContent className="overflow-x-auto px-5 py-0">
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead className="border-b border-slate-100 text-[11px] uppercase tracking-[0.18em] text-slate-400 dark:border-slate-700/40 dark:text-slate-300">
              <tr>
                <th className="py-3 pr-4">User</th>
                <th className="py-3 pr-4">Role</th>
                <th className="py-3 pr-4">Contact</th>
                {showParentColumn ? <th className="py-3 pr-4">Parent</th> : null}
                <th className="py-3 pr-4">Children</th>
                <th className="py-3 pr-4">Access</th>
                <th className="py-3 pr-4">Created</th>
                <th className="py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/40">
              {filteredUsers.map((user) => (
                <tr
                  key={user.id}
                  className={cn(
                    'transition hover:bg-slate-50 dark:bg-slate-900/80 dark:hover:bg-slate-800/80',
                    user.isDeleted ? 'opacity-70' : ''
                  )}
                >
                  <td className="py-4 pr-4">
                    <button
                      className="text-left"
                      onClick={() =>
                        navigate(`/users/${user.id}`, user.role === 'CHILD' ? { state: { fromParentId: user.parentId } } : undefined)
                      }
                    >
                      <p className="font-semibold text-slate-900 dark:text-slate-100">
                        {user.role === 'CHILD' ? getAdminChildDisplayName(user) : user.name || user.email}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-300">{user.id}</p>
                    </button>
                  </td>
                  <td className="py-4 pr-4">
                    <Badge className="rounded-full bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100">
                      {roleLabels[user.role]}
                    </Badge>
                  </td>
                  <td className="max-w-[260px] truncate py-4 pr-4 text-xs text-slate-500 dark:text-slate-300">{user.email || '—'}</td>
                  {showParentColumn ? (
                    <td className="py-4 pr-4 text-xs text-slate-500 dark:text-slate-300">
                      {user.role === 'CHILD' ? (
                        <button
                          className="font-semibold text-blue-600 hover:underline dark:text-blue-300"
                          onClick={() => user.parentId && navigate(`/users/${user.parentId}`)}
                        >
                          {user.parentName || user.parentEmail || 'Parent'}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  ) : null}
                  <td className="py-4 pr-4 text-xs text-slate-500 dark:text-slate-300">
                    {user.role === 'PARENT' ? (
                      <button
                        className="font-semibold text-blue-600 hover:underline dark:text-blue-300"
                        onClick={() => {
                          setRoleFilter('CHILD');
                          setParentFilterId(user.id);
                          setPageOffset(0);
                        }}
                      >
                        {user.childrenCount ?? 0} children
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-4 pr-4">
                    {user.isDeleted ? (
                      <Badge className="rounded-full bg-rose-600 text-white">Deleted</Badge>
                    ) : user.role === 'PARENT' && user.isVerified === false ? (
                      <Badge className="rounded-full bg-sky-600 text-white">Unverified</Badge>
                    ) : user.role !== 'CHILD' && user.approved === false ? (
                      <Badge className="rounded-full bg-amber-500 text-white">Pending</Badge>
                    ) : (
                      <Badge className="rounded-full bg-emerald-600 text-white">
                        {user.role === 'CHILD' ? 'Active' : 'Approved'}
                      </Badge>
                    )}
                  </td>
                  <td className="whitespace-nowrap py-4 pr-4 text-sm text-slate-500 dark:text-slate-300">
                    {new Intl.DateTimeFormat('en-US', {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    }).format(new Date(user.createdAt))}
                  </td>
                  <td className="py-4 text-right">
                    <div className="flex items-center justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-9 w-9 rounded-xl border-slate-200 bg-white p-0 text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-950 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
                            aria-label={`Open actions for ${user.email || user.name || 'user'}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 rounded-2xl p-2">
                          <DropdownMenuLabel className="px-2 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                            Account actions
                          </DropdownMenuLabel>
                          <DropdownMenuItem
                            className="cursor-pointer rounded-xl"
                            onSelect={() =>
                              navigate(`/users/${user.id}`, user.role === 'CHILD' ? { state: { fromParentId: user.parentId } } : undefined)
                            }
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit account
                          </DropdownMenuItem>
                          {!user.isDeleted && user.role === 'PARENT' && user.isVerified === false ? (
                            <DropdownMenuItem
                              className="cursor-pointer rounded-xl"
                              disabled={resendVerificationPendingId === user.id}
                              onSelect={() => handleResendVerification(user)}
                            >
                              <Mail className="mr-2 h-4 w-4" />
                              {resendVerificationPendingId === user.id ? 'Sending verification...' : 'Send verification email'}
                            </DropdownMenuItem>
                          ) : null}
                          {!user.isDeleted && user.role === 'PARENT' && user.isVerified !== false && user.approved === false ? (
                            <DropdownMenuItem
                              className="cursor-pointer rounded-xl"
                              disabled={resendConsentPendingId === user.id}
                              onSelect={() => handleResendConsent(user)}
                            >
                              <FileCheck2 className="mr-2 h-4 w-4" />
                              {resendConsentPendingId === user.id ? 'Sending consent link...' : 'Send consent link'}
                            </DropdownMenuItem>
                          ) : null}
                          {!user.isDeleted && user.role !== 'CHILD' && user.role !== 'PARENT' && user.approved === false ? (
                            <DropdownMenuItem
                              className="cursor-pointer rounded-xl"
                              disabled={statusActionPending}
                              onSelect={() => handleApprove(user)}
                            >
                              <CheckCircle2 className="mr-2 h-4 w-4" />
                              Approve account
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuSeparator className="my-2" />
                          {user.isDeleted ? (
                            <DropdownMenuItem className="cursor-pointer rounded-xl text-emerald-700" onSelect={() => handleRestore(user)}>
                              <RotateCcw className="mr-2 h-4 w-4" />
                              Restore account
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              className="cursor-pointer rounded-xl text-rose-600 focus:bg-rose-50 focus:text-rose-700 dark:focus:bg-rose-500/10"
                              onSelect={() => {
                                setDeleteTarget(user);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete account
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}

              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-slate-500 dark:text-slate-300">
                    {deletedFilter === 'deleted' ? 'No deleted users found.' : 'No users match your query.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {pagination ? (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-300">
              <span>
                {pagination.total} users • Page {Math.floor(pagination.offset / pagination.limit) + 1}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-full"
                  disabled={pagination.offset === 0}
                  onClick={() => setPageOffset((prev) => Math.max(prev - pagination.limit, 0))}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-full"
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                  onClick={() => setPageOffset((prev) => prev + pagination.limit)}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Delete account</DialogTitle>
            <DialogDescription>
              {deleteTarget?.role === 'PARENT'
                ? 'Deleting this parent will also delete all active child accounts linked to them.'
                : 'This will remove access for the selected account.'}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-2xl border border-rose-200/60 bg-rose-50/70 p-4 text-sm text-rose-700">
            This action is reversible by restoring the account later, but access is removed immediately.
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" className="rounded-full" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="rounded-full bg-rose-600 text-white hover:bg-rose-500" onClick={handleDeleteConfirm}>
              Delete account
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ title, value, icon }: { title: string; value: number; icon?: ReactNode }) {
  return (
    <Card className="border border-slate-100 dark:border-slate-700/40 bg-white/90 dark:bg-slate-900/80 shadow-sm">
      <CardContent className="flex items-center justify-between px-5 py-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400 dark:text-slate-300">{title}</p>
          <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{value}</p>
        </div>
        {icon && <div className="rounded-full bg-primary/10 p-2 text-primary">{icon}</div>}
      </CardContent>
    </Card>
  );
}

function SkeletonHeader() {
  return (
    <div className="h-20 rounded-3xl border border-dashed border-slate-200 dark:border-slate-700/40 bg-white/50 dark:bg-slate-900/60" />
  );
}
